/**
 * The archetypes as the operation generator sees them (03 §2, 10 §2): base threat, home terrain, role.
 * The 1987 twelve are below; the expansion's twenty-four come from archetypesExpansion.ts; what a mission
 * IS beyond those numbers (category, quiet rule, how it ends, what it needs) comes from archetypeFacts.ts.
 */

import { type ArchetypeId, type Biome, isOriginalArchetype } from '../../sim/types';
import { type ArchetypeFacts, CATEGORIES, FACTS } from './archetypeFacts';
import { EXPANSION_ARCHETYPE_ROWS } from './archetypesExpansion';

/** What a table row states; the rest of `ArchetypeInfo` is joined on from FACTS. */
export interface ArchetypeRow {
  id: ArchetypeId;
  name: string;
  /** The one line a card has room for: the goal and the special rule. */
  text: string;
  /** Base threat rating before difficulty and readiness (02 §12). */
  baseThreat: number;
  native: Biome;
  /** Eligible as a theatre finale. */
  keystone: boolean;
}

export interface ArchetypeInfo extends ArchetypeRow {
  /** 1-12: the manual's order. 13-36: the expansion's (10 §2). */
  number: number;
  /** Noise before the goal zone fails or ruins the mission: a loud player must be able to route round it. */
  strictQuiet: boolean;
  facts: ArchetypeFacts;
}

const ORIGINAL_ARCHETYPE_ROWS: readonly ArchetypeRow[] = [
  { id: 'munitions-depot', name: 'Destroy a Munitions Depot', baseThreat: 2, native: 'desert', keystone: true,
    text: 'Destroy the ammo shack, the explosives magazine and the fuel dump. Loud is fine.' },
  { id: 'code-book', name: 'Steal a Code Book', baseThreat: 4, native: 'temperate', keystone: false,
    text: 'Reach the radio in the comms post of a ruined village. The garrison expects you.' },
  { id: 'enemy-aircraft', name: 'Disable Enemy Aircraft', baseThreat: 4, native: 'arctic', keystone: true,
    text: 'Wreck every parked aircraft. Raise the alarm outside the wire and the jets scramble.' },
  { id: 'capture-officer', name: 'Capture an Enemy Officer', baseThreat: 3, native: 'desert', keystone: false,
    text: 'Find the officer among the HQ tents, take him alive and get him aboard.' },
  { id: 'pipeline', name: 'Cut a Pipeline', baseThreat: 5, native: 'temperate', keystone: true,
    text: 'Only a time bomb kills the pumping station. Expect minefields and minitanks.' },
  { id: 'radar-array', name: 'Knock Out Enemy Radar Array', baseThreat: 5, native: 'arctic', keystone: false,
    text: 'Cross the water barrier and destroy at least half the antennas. The clock is tight.' },
  { id: 'sam-site', name: 'Disable a SAM Site', baseThreat: 5, native: 'desert', keystone: true,
    text: 'Knife only until inside the goal zone. Automated mini-bunkers guard the launchers.' },
  { id: 'pow-camp', name: 'Liberate a P.O.W. Camp', baseThreat: 5, native: 'temperate', keystone: false,
    text: 'Open the tiger pits and bring the prisoners out. Success frees every captured ranger.' },
  { id: 'photograph-aircraft', name: 'Photograph a Secret Aircraft', baseThreat: 4, native: 'arctic', keystone: false,
    text: 'Stay inside the hangar for twenty uninterrupted seconds. A uniform helps.' },
  { id: 'hostages', name: 'Free the Hostages', baseThreat: 6, native: 'desert', keystone: true,
    text: 'Blow the prison door and get the hostages out before the executioners arrive.' },
  { id: 'diversion', name: 'Create a Diversion', baseThreat: 6, native: 'temperate', keystone: false,
    text: 'Lie low until the signal, then fight north to the border. No early recall.' },
  { id: 'delayed-sabotage', name: 'Delayed Sabotage', baseThreat: 5, native: 'arctic', keystone: false,
    text: 'Plant a bomb at the aviation fuel dump without being noticed near it.' },
];

/** All thirty-six, in mission-number order: what Operations, Practice, the Daily Drop and `describe` draw on. */
export const ARCHETYPES: readonly ArchetypeInfo[] = [...ORIGINAL_ARCHETYPE_ROWS, ...EXPANSION_ARCHETYPE_ROWS]
  .map((row, i) => ({ ...row, number: i + 1, strictQuiet: FACTS[row.id].quiet === 'strict', facts: FACTS[row.id] }));

/** One of the 1987 twelve: Classic mode flies only these, and every theatre keeps some of them (10 §4). */
export const isOriginal = (info: Pick<ArchetypeInfo, 'id'>): boolean => isOriginalArchetype(info.id);

export const ARCHETYPE_IDS: readonly ArchetypeId[] = ARCHETYPES.map((a) => a.id);

const BY_ID = new Map<ArchetypeId, ArchetypeInfo>(ARCHETYPES.map((a) => [a.id, a]));

/** Table row for an archetype. */
export function archetype(id: ArchetypeId): ArchetypeInfo {
  const info = BY_ID.get(id);
  if (!info) throw new Error(`Unknown archetype ${id}`);
  return info;
}

/**
 * How the pickers and the records table group the missions: the 1987 twelve first, under their own heading,
 * then the expansion by category (10 §2).
 */
export const MISSION_GROUPS = ['1987', ...CATEGORIES] as const;
export type MissionGroup = (typeof MISSION_GROUPS)[number];

export const groupOf = (info: Pick<ArchetypeInfo, 'id' | 'facts'>): MissionGroup =>
  (isOriginal(info) ? '1987' : info.facts.category);

export const GROUP_1987 = {
  name: '1987', text: "The original twelve, in the manual's order. Classic mode flies only these.",
} as const;

export const BIOMES: readonly Biome[] = ['arctic', 'desert', 'temperate'];

export const BIOME_NAMES: Record<Biome, string> = { arctic: 'Arctic', desert: 'Desert', temperate: 'Temperate' };
